import React, { useState, useEffect, useContext } from 'react';
import { Link, Redirect, useLocation, useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { touch } from 'redux-form';
import { useSelector, useDispatch } from 'react-redux';
import AuthWrapper from 'components/AuthWrapper';
import userShape from 'shapes/userShape';
import * as selectors from 'redux-modules/auth/selectors';
import locationShape from 'shapes/locationShape';
import getInitialValuesFromQuery from 'helpers/getInitialValuesFromQuery';
import redirect from 'helpers/redirect';
import { normalizePhone } from 'components/fields/PhoneNumberField';
import SignInForm from './SignInForm';
import ssoIntegrationOptionsShape from 'shapes/ssoIntegrationOptionsShape';
import HpaStoreContext from 'contexts/HpaStoreContext';
import FormatErrorMessage from '../FormatMessage/FormatErrorMessage';
import AuthWrapperFooter from 'components/AuthWrapperFooter';
import identityClient from 'clients/identityClient';
import { initializePendo } from '../../helpers/pendo';
import {
  shouldSuppressRegister,
  isNewSignupWorkflow,
} from '../../helpers/customBranding';
import { buildSignUpSearchFromLocation } from '../../helpers/buildSignUpSearchFromLocation';

const SignIn = ({
  location,
  handleRememberMeChange,
  ssoIntegrationOptions,
  username,
}) => {
  const [primaryUserName, setPrimaryUserName] = useState('');
  const [authError, setAuthError] = useState(null);
  const [errorStatus, updateErrorStatus] = useState(undefined);
  const [recoverError, setRecoverError] = useState(undefined);
  const [recoverEmailResponse, setRecoverEmailResponse] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useDispatch();
  const oauthError = useSelector(selectors.getOAuthError);

  const context = useContext(HpaStoreContext);
  const locationHook = useLocation();
  const history = useHistory();
  const params = locationHook.search;

  const getBrandingDetail = (name) => {
    return context && Array.isArray(context.customBranding)
      ? context.customBranding.find(
          (detail) =>
            detail.name === name &&
            (detail.text === 'customBranding' ||
              detail.text === null ||
              detail.text === undefined),
        )
      : undefined;
  };
  const customDisplayName = getBrandingDetail('displayName');
  const shouldSuppressRegisterFlag = shouldSuppressRegister(context);
  const isNewSignupWorkflowEnabled = isNewSignupWorkflow(context);

  if (context.username !== primaryUserName && primaryUserName.includes('@')) {
    context.setData({ username: primaryUserName });
  } else if (
    primaryUserName &&
    !primaryUserName.includes('@') &&
    context.username
  ) {
    context.setData({ username: '' });
  }

  useEffect(() => {
    setAuthError(null);
    updateErrorStatus(undefined);
    setRecoverError(undefined);
    setRecoverEmailResponse('');
  }, [location]);

  useEffect(() => {
    const su = locationHook?.state?.username;
    const fromQuery = getInitialValuesFromQuery(params || '', {
      username: '',
    }).username;
    const preferred = su || fromQuery;
    if (preferred) {
      context.setData({ username: preferred });
    }
  }, [locationHook.state?.username, params]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleVerifyLoginUsername = async (username, recoverUsername) => {
    try {
      const queryString = params ? `${params}` : '';
      await identityClient
        .validateUsername(username, queryString)
        .then(async (response) => {
          const getAccountData = await identityClient.getAccountInfo(
            response.hstreamId,
          );
          initializePendo(getAccountData);
        });
    } catch (err) {
      updateErrorStatus(err.response && err.response.status);
      if (err.response && err.response.status === 400) {
        try {
          const response = await identityClient.recoverPrimaryEmail(
            recoverUsername,
          );
          setRecoverEmailResponse(response);
        } catch (recoverErr) {
          if (
            recoverErr.response &&
            recoverErr.response.status >= 400 &&
            recoverErr.response.status <= 499
          ) {
            setRecoverError('The username you have entered is invalid.');
            setRecoverEmailResponse('');
          } else {
            console.error('Failed to recover primary email:', recoverErr);
          }
        }
      }
      throw err;
    }
  };

  const onSubmit = async (values) => {
    if (isLoading) return;
    setIsLoading(true);
    handleRememberMeChange(values.remember_me);
    if (!values?.username) {
      dispatch(touch('sign-in', 'username'));
      setIsLoading(false);
      return;
    }
    if (values?.username) {
      values.username = values.username.trim();
    }
    let recoverUsername;
    if (values?.username?.indexOf('@') === -1) {
      values.username = normalizePhone(values.username);
      let encoded_phone = encodeURIComponent(values.username);
      recoverUsername = encoded_phone;
    } else {
      recoverUsername = values.username;
    }

    setPrimaryUserName(recoverUsername);
    setAuthError(null);
    try {
      await handleVerifyLoginUsername(values.username, recoverUsername);
      history.push({
        pathname: '/password',
        search: params,
        state: {
          primaryUserName: values.username,
          newSignupFlow: { value: isNewSignupWorkflowEnabled ? '1' : '0' },
        },
      });
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const getInitialValues = () => {
    const stateUsername = locationHook?.state?.username;
    const initialValuesFromQuery = getInitialValuesFromQuery(params, {
      email: '',
      username: '',
      password: '',
    });

    if (stateUsername) {
      initialValuesFromQuery.username = stateUsername;
    } else if (!initialValuesFromQuery.username) {
      if (username) {
        initialValuesFromQuery.username = username;
      } else if (context.username) {
        initialValuesFromQuery.username = context.username;
      }
    }
    initialValuesFromQuery.remember_me = ssoIntegrationOptions.rememberMe.value;
    return initialValuesFromQuery;
  };

  const toRegister = {
    pathname: isNewSignupWorkflowEnabled ? '/account/signup' : '/signup',
    search: buildSignUpSearchFromLocation(
      locationHook.search || location.search,
      {
        ...ssoIntegrationOptions,
        username:
          locationHook.state?.username ||
          context.username ||
          ssoIntegrationOptions.username,
      },
    ),
  };

  const redirectName = redirect.parseRedirectName(location);

  if (oauthError && oauthError.status !== 401) {
    return (
      <Redirect
        to={{
          pathname: '/authorize',
          state: { oauthSignInError: true },
        }}
      />
    );
  }

  return (
    <div>
      <AuthWrapper
        title="Please enter your hStream ID to sign in"
        redirectName={
          customDisplayName && customDisplayName.value
            ? customDisplayName.value
            : redirectName
        }
        footer={<AuthWrapperFooter />}
      >
        {(recoverEmailResponse || recoverError) && (
          <FormatErrorMessage
            error="The username you have entered is invalid."
            recoverEmail={recoverEmailResponse}
            recoverError={recoverError}
            params={params}
            errorStatus={errorStatus}
            newSignupFlow={isNewSignupWorkflowEnabled}
          />
        )}

        <SignInForm
          initialValues={getInitialValues()}
          onSubmit={onSubmit}
          location={location}
          rememberMeValues={ssoIntegrationOptions.rememberMe}
          isLoading={isLoading}
        />

        {(shouldSuppressRegisterFlag === undefined ||
          shouldSuppressRegisterFlag === null ||
          shouldSuppressRegisterFlag === false) && (
          <>
            <hr />
            <section className="d-flex align-items-center">
              <span style={{ marginRight: '.5em' }}>Need to sign up?</span>
              <Link
                to={toRegister}
                tabIndex={isLoading ? -1 : 0}
                aria-disabled={isLoading || undefined}
                onClick={(e) => {
                  if (isLoading) e.preventDefault();
                }}
              >
                Register Now
              </Link>
            </section>
          </>
        )}
      </AuthWrapper>
    </div>
  );
};

SignIn.propTypes = {
  location: locationShape.isRequired,
  handleRememberMeChange: PropTypes.func.isRequired,
  ssoIntegrationOptions: ssoIntegrationOptionsShape.isRequired,
  username: PropTypes.string,
  user: userShape,
  errorMessage: PropTypes.string,
  oauthError: PropTypes.shape({
    status: PropTypes.number,
  }),
  signInError: PropTypes.shape({
    status: PropTypes.number,
  }),
  promptForPhone: PropTypes.bool,
  recoverEmail: PropTypes.string,
  recoverErrorCode: PropTypes.bool,
};

SignIn.defaultProps = {
  user: undefined,
  errorMessage: undefined,
  oauthError: undefined,
  signInError: undefined,
  promptForPhone: undefined,
  recoverEmail: undefined,
  recoverErrorCode: undefined,
};

export default SignIn;
